import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import {
  Gift,
  Heart,
  Zap,
  Leaf,
  TrendingUp,
  GraduationCap,
  Wallet,
  Briefcase,
  Sprout,
  Globe,
  Building2,
  Shield,
  Users,
  ArrowRight,
  Check,
} from "lucide-react"
import Layout from "@/components/Layout"
import AssistantCard from "@/components/AssistantCard"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ASSISTANTS_LIST, BUSINESS_SOLUTIONS, ROUTE_PATHS } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion"
import { IMAGES } from "@/assets/images"

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Gift,
  Heart,
  Zap,
  Leaf,
  TrendingUp,
  GraduationCap,
  Wallet,
  Briefcase,
  Sprout,
  Globe,
  Building2,
  Shield,
  Users,
}

const audienceAssistants = {
  Bank: [
    ASSISTANTS_LIST.find((a) => a.id === "money-coach")!,
    ASSISTANTS_LIST.find((a) => a.id === "my-debt-help")!,
    ASSISTANTS_LIST.find((a) => a.id === "my-job-career")!,
  ],
  Insurer: [
    ASSISTANTS_LIST.find((a) => a.id === "family-health-guide")!,
    ASSISTANTS_LIST.find((a) => a.id === "home-fix-build")!,
    ASSISTANTS_LIST.find((a) => a.id === "elderly-care")!,
  ],
  Retail: [
    ASSISTANTS_LIST.find((a) => a.id === "healthy-food")!,
    ASSISTANTS_LIST.find((a) => a.id === "my-garden")!,
    ASSISTANTS_LIST.find((a) => a.id === "earn-extra-cash")!,
  ],
  Employer: [
    ASSISTANTS_LIST.find((a) => a.id === "mental-support")!,
    ASSISTANTS_LIST.find((a) => a.id === "my-job-career")!,
    ASSISTANTS_LIST.find((a) => a.id === "my-life-coach")!,
  ],
}

const launchSteps = [
  { number: 1, title: "Choose Audience", description: "Select your customer segment" },
  { number: 2, title: "Choose Assistants", description: "Pick relevant support areas" },
  { number: 3, title: "Launch", description: "Deploy in days, not months" },
]

const brandingOptions = [
  {
    title: "Under Your Brand",
    description: "Full white-label with your branding",
    icon: Building2,
  },
  {
    title: "Co-branded",
    description: "Powered by Sammo, your brand front",
    icon: Users,
  },
  {
    title: "Campaign Branded",
    description: "Custom branding for specific campaigns",
    icon: Zap,
  },
  {
    title: "Country Branded",
    description: "Localised branding for your market",
    icon: Globe,
  },
]

export default function ForBusiness() {
  return (
    <Layout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="min-h-screen"
      >
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden bg-primary text-primary-foreground">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-primary opacity-60" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${IMAGES.BUSINESS_TEAM_8})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              className="max-w-4xl mx-auto text-center space-y-8"
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
                Turn customer relationships into everyday value
              </h1>
              <p className="text-xl md:text-2xl text-primary-foreground/90 max-w-3xl mx-auto">
                Launch practical household support for your customers, members, or employees.
                Increase engagement, retention, and lifetime value through meaningful everyday
                assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6"
                >
                  <Link to={ROUTE_PATHS.CONTACT}>Book a Demo</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20 text-lg px-8 py-6"
                >
                  <Link to={ROUTE_PATHS.PLATFORM}>See How It Works</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp}>
              <SectionHeader
                eyebrow="Business Solutions"
                headline="Deploy support that drives real value"
                subheadline="Choose the solution that fits your business goals and customer needs"
                align="center"
                className="mb-16"
              />
            </motion.div>
            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {BUSINESS_SOLUTIONS.map((solution) => {
                const IconComponent = iconMap[solution.icon]
                return (
                  <motion.div
                    key={solution.id}
                    variants={staggerItem}
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={springPresets.snappy}
                    className="bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6">
                      {IconComponent && (
                        <IconComponent className="w-7 h-7 text-accent" />
                      )}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{solution.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {solution.description}
                    </p>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp}>
              <SectionHeader
                eyebrow="Customise Your Offering"
                headline="Choose your assistants"
                subheadline="Select the support areas most relevant to your audience"
                align="center"
                className="mb-12"
              />
            </motion.div>
            <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
              <Tabs defaultValue="Bank" className="w-full">
                <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12 h-auto p-2 bg-card rounded-2xl">
                  <TabsTrigger
                    value="Bank"
                    className="text-base py-3 rounded-xl data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    Bank
                  </TabsTrigger>
                  <TabsTrigger
                    value="Insurer"
                    className="text-base py-3 rounded-xl data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    Insurer
                  </TabsTrigger>
                  <TabsTrigger
                    value="Retail"
                    className="text-base py-3 rounded-xl data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    Retail
                  </TabsTrigger>
                  <TabsTrigger
                    value="Employer"
                    className="text-base py-3 rounded-xl data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                  >
                    Employer
                  </TabsTrigger>
                </TabsList>
                {Object.entries(audienceAssistants).map(([key, assistants]) => (
                  <TabsContent key={key} value={key} className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {assistants.map((assistant) => (
                        <AssistantCard key={assistant.id} assistant={assistant} />
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div variants={fadeInUp}>
              <SectionHeader
                eyebrow="Launch Model"
                headline="From concept to launch in weeks"
                subheadline="Simple, fast deployment with flexible branding options"
                align="center"
                className="mb-16"
              />
            </motion.div>
            <motion.div variants={staggerContainer} className="max-w-5xl mx-auto space-y-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {launchSteps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    variants={staggerItem}
                    className="relative"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-20 h-20 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                        {step.number}
                      </div>
                      <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                      <p className="text-muted-foreground">{step.description}</p>
                    </div>
                    {index < launchSteps.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-border">
                        <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-accent" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="pt-8">
                <h3 className="text-3xl font-semibold text-center mb-12">
                  Flexible Branding Options
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {brandingOptions.map((option) => {
                    const IconComponent = option.icon
                    return (
                      <motion.div
                        key={option.title}
                        variants={staggerItem}
                        whileHover={{ scale: 1.02 }}
                        transition={springPresets.snappy}
                        className="bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-accent" />
                          </div>
                          <div>
                            <h4 className="text-xl font-semibold mb-2">{option.title}</h4>
                            <p className="text-muted-foreground">{option.description}</p>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${IMAGES.PROFESSIONAL_2})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/50" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              variants={fadeInUp}
              className="max-w-4xl mx-auto text-center space-y-8"
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Build a benefit people actually use
              </h2>
              <p className="text-xl md:text-2xl text-primary-foreground/90">
                Stop offering benefits that sit unused. Launch practical support that drives
                engagement, retention, and measurable value.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6"
                >
                  <Link to={ROUTE_PATHS.CONTACT}>Book a Demo</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20 text-lg px-8 py-6"
                >
                  <Link to={ROUTE_PATHS.ASSISTANTS}>Explore Assistants</Link>
                </Button>
              </div>
              <div className="pt-8 flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-accent" />
                  <span>Launch in weeks</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-accent" />
                  <span>White-label ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-accent" />
                  <span>Measurable impact</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </Layout>
  )
}
